"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MWaterAccountingSystemListComponent = MWaterAccountingSystemListComponent;
const lodash_1 = __importDefault(require("lodash"));
const expressions_1 = require("@mwater/expressions");
const react_1 = require("react");
const react_2 = __importDefault(require("react"));
const UIComponents_1 = require("../UIComponents");
const bootstrap_1 = require("@mwater/react-library/lib/bootstrap");
function MWaterAccountingSystemListComponent(props) {
    const { apiUrl, schema, client, onChange, extraTables, onExtraTableAdd, onExtraTableRemove, locale } = props;
    const [rawAccountingSystems, setRawAccountingSystems] = (0, react_1.useState)();
    const [accountingTablesetDesign, setAccountingTablesetDesign] = (0, react_1.useState)();
    const [search, setSearch] = (0, react_1.useState)("");
    // Stores the fully qualified table ID that we want to select after its system is loaded
    const [targetSelection, setTargetSelection] = (0, react_1.useState)();
    // Fetch accounting systems (partitions) and the main accounting tableset design
    (0, react_1.useEffect)(() => {
        // Fetch rows from the 'custom.accounting.partitions' table
        fetch(`${apiUrl}custom/accounting/partitions?client=${client || ""}`)
            .then((response) => {
            if (!response.ok) {
                console.error(`Error fetching accounting system partitions: ${response.status} ${response.statusText}`);
                return []; // Return empty array on error
            }
            return response.json();
        })
            .then((systems) => {
            setRawAccountingSystems(systems);
        })
            .catch(error => {
            console.error("Failed to load accounting systems:", error);
            setRawAccountingSystems([]);
        });
        // Fetch the design of the 'custom.accounting' tableset
        const filter = encodeURIComponent(JSON.stringify({ code: "accounting" }));
        fetch(`${apiUrl}custom_tablesets?filter=${filter}&client=${client || ""}`)
            .then((response) => {
            if (!response.ok) {
                console.error(`Error fetching accounting tableset design: ${response.status} ${response.statusText}`);
                return null;
            }
            return response.json();
        })
            .then((tablesets) => {
            if (tablesets && tablesets.length > 0 && tablesets[0].design) {
                setAccountingTablesetDesign(tablesets[0].design);
            }
            else {
                console.error("Accounting tableset design not found or invalid structure.");
            }
        })
            .catch(error => {
            console.error("Failed to load accounting tableset design:", error);
        });
    }, [apiUrl, client]);
    const sortedAccountingSystems = (0, react_1.useMemo)(() => {
        if (!rawAccountingSystems) {
            return undefined;
        }
        return lodash_1.default.sortByAll(rawAccountingSystems, [
            (sys) => {
                const wildcard = `custom.accounting:${String(sys._partition)}.*`;
                return !extraTables.includes(wildcard); // false (0) for included, true (1) for not
            },
            (sys) => sys.name
        ]);
    }, [rawAccountingSystems, extraTables]);
    // Effect to handle selecting a table once its accounting system's tables are loaded into the schema
    (0, react_1.useEffect)(() => {
        if (targetSelection && schema.getTable(targetSelection)) {
            onChange(targetSelection);
            setTargetSelection(undefined); // Clear the target selection
        }
    }, [targetSelection, schema, onChange]);
    const handleSelectTable = (partitionId, table) => {
        const qualifiedTableId = `custom.accounting:${String(partitionId)}.${table.id}`;
        const accountingSystemWildcard = `custom.accounting:${String(partitionId)}.*`;
        if (!extraTables.includes(accountingSystemWildcard)) {
            onExtraTableAdd(accountingSystemWildcard);
        }
        if (schema.getTable(qualifiedTableId)) {
            onChange(qualifiedTableId);
        }
        else {
            // If not yet in schema, ensure add request is made and set for future selection
            if (!extraTables.includes(accountingSystemWildcard)) {
                onExtraTableAdd(accountingSystemWildcard);
            }
            setTargetSelection(qualifiedTableId);
        }
    };
    const handleRemoveAccountingSystem = (partitionId) => {
        const accountingSystemWildcard = `custom.accounting:${String(partitionId)}.*`;
        if (confirm(T `Remove this accounting system? Some widgets may not work correctly.`)) {
            onExtraTableRemove(accountingSystemWildcard);
        }
    };
    if (!sortedAccountingSystems || !accountingTablesetDesign) {
        return (react_2.default.createElement("div", null,
            react_2.default.createElement("i", { className: "fa fa-spin fa-spinner" }),
            " ",
            T `Loading accounting data...`));
    }
    const filteredSystems = sortedAccountingSystems.filter(system => {
        if (search === "") {
            return true;
        }
        const systemName = expressions_1.ExprUtils.localizeString(system.name, locale) || "";
        if (systemName.toLowerCase().includes(search.toLowerCase())) {
            return true;
        }
        return accountingTablesetDesign.tables.some(t => !t.deprecated && (expressions_1.ExprUtils.localizeString(t.name, locale) || "").toLowerCase().includes(search.toLowerCase()));
    });
    const renderSingleAccountingSystem = (system) => {
        const systemName = expressions_1.ExprUtils.localizeString(system.name, locale) || T `Unnamed System (${system._partition})`;
        const accountingSystemWildcard = `custom.accounting:${String(system._partition)}.*`;
        const isIncluded = extraTables.includes(accountingSystemWildcard);
        const tablesForThisSystem = accountingTablesetDesign.tables
            .filter(t => !t.deprecated)
            .filter(t => t.id != "partitions" && t.id != "roles")
            .filter(t => {
            if (search === "") {
                return true;
            }
            if ((expressions_1.ExprUtils.localizeString(system.name, locale) || "").toLowerCase().includes(search.toLowerCase())) {
                return true;
            }
            return (expressions_1.ExprUtils.localizeString(t.name, locale) || "").toLowerCase().includes(search.toLowerCase());
        })
            .map(t => ({
            name: expressions_1.ExprUtils.localizeString(t.name, locale),
            onClick: () => handleSelectTable(system._partition, t)
        }));
        if (search !== "" && !(expressions_1.ExprUtils.localizeString(system.name, locale) || "").toLowerCase().includes(search.toLowerCase()) && tablesForThisSystem.length === 0) {
            return null;
        }
        return (react_2.default.createElement("div", { key: system._partition, className: "mt-3" },
            react_2.default.createElement("div", { className: "d-flex justify-content-between align-items-center" },
                react_2.default.createElement("h4", { className: "text-muted mb-1" }, systemName),
                isIncluded && (react_2.default.createElement("button", { className: "btn btn-sm btn-link", type: "button", onClick: () => handleRemoveAccountingSystem(system._partition), title: T `Remove this accounting system` },
                    react_2.default.createElement("i", { className: "fa fa-times" })))),
            react_2.default.createElement(UIComponents_1.OptionListComponent, { items: tablesForThisSystem })));
    };
    return (react_2.default.createElement("div", null,
        react_2.default.createElement(bootstrap_1.TextInput, { value: search, onChange: setSearch, placeholder: T `Search accounting systems or tables...` }),
        targetSelection && !schema.getTable(targetSelection) && (react_2.default.createElement("div", { className: "alert alert-info my-2" },
            react_2.default.createElement("i", { className: "fa fa-spinner fa-spin me-2" }),
            T `Loading tables for selected accounting system...`)),
        filteredSystems.length > 0
            ? filteredSystems.map(system => renderSingleAccountingSystem(system))
            : (sortedAccountingSystems.length > 0 && search !== "" && react_2.default.createElement("p", null, T `No accounting systems or tables match your search.`)),
        sortedAccountingSystems.length === 0 && !targetSelection && react_2.default.createElement("p", null, T `No accounting systems found.`)));
}
